import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Scale, X, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useCompare, MAX_COMPARE } from '../context/CompareContext';
import { useLanguage } from '../context/LanguageContext';

export default function CompareTray() {
  const { items, remove, clear } = useCompare();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (items.length === 0 || pathname === '/compare') return null;

  const go = () => navigate(`/compare?grades=${items.map(i => i.slug).join(',')}`);

  return (
    <div
      data-testid="compare-tray"
      className="fixed bottom-0 left-0 right-0 z-[55] bg-surface/95 backdrop-blur-md border-t border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.25)]"
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-white shrink-0">
          <Scale className="w-4 h-4 text-blue-brand" />
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/60">
            {t('compare.tray')} {items.length}/{MAX_COMPARE}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          {items.map(item => (
            <span
              key={item.slug}
              data-testid={`compare-chip-${item.slug}`}
              className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1.5"
            >
              {item.name}
              <button
                data-testid={`compare-chip-remove-${item.slug}`}
                onClick={() => remove(item.slug)}
                aria-label="Remove"
                className="text-white/50 hover:text-white transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            data-testid="compare-tray-clear"
            onClick={clear}
            className="text-[11px] font-semibold uppercase tracking-wider text-white/45 hover:text-white transition-colors"
          >
            {t('compare.clear')}
          </button>
          {items.length < 2 ? (
            <span className="text-[11px] text-white/40 max-w-[180px] leading-snug">
              {t('compare.needTwo')}
            </span>
          ) : (
            <button
              data-testid="compare-tray-go"
              onClick={go}
              className="inline-flex items-center gap-2 bg-blue-brand text-white text-xs font-bold uppercase tracking-wider px-5 py-3 hover:bg-white hover:text-ink transition-colors"
            >
              {t('compare.cta')} <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const CompareButton = ({ grade, className = '', testIdSuffix = '' }) => {
  const { has, toggle } = useCompare();
  const { t } = useLanguage();
  const active = has(grade.slug);

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggle(grade) === 'full') toast.error(t('compare.full'));
  };

  return (
    <button
      type="button"
      data-testid={`compare-toggle-${grade.slug}${testIdSuffix}`}
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
        active ? 'text-blue-brand' : 'text-gray-400 hover:text-ink'
      } ${className}`}
    >
      <Scale className="w-3.5 h-3.5" />
      {active ? t('compare.added') : t('compare.add')}
    </button>
  );
};

export const CompareLink = () => {
  const { t } = useLanguage();
  return (
    <Link
      to="/compare"
      data-testid="compare-page-link"
      className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-brand hover:text-ink transition-colors"
    >
      <Scale className="w-3.5 h-3.5" /> {t('compare.tool')}
    </Link>
  );
};
